import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Building2, TrendingUp, PlusCircle, MapPin, Shield, Users, Clock } from 'lucide-react';
import NewsletterSignup from '../components/NewsletterSignup';

/**
 * HomeTasteSkillV2 - Premium B2B Marketplace Version
 * 
 * Verbesserungen gegenüber V1:
 * - Realistischere Marktplatz-Vorschau statt abstrakter Grafik
 * - Stärkere visuelle Hierarchie im Hero
 * - Reduzierte Leerflächen, bessere Proportionen
 * - Verfeinerte Service-Karten mit besserer Typografie
 * - Hochwertigeres, seriöseres Gesamtbild
 */

const HomeTasteSkillV2 = () => {
  // Beispiel-Listings für die Vorschau (keine echten Daten)
  const previewListings = [
    {
      type: 'Handel & Distribution',
      location: 'NRW',
      year: '2019',
      status: 'Verfügbar',
    },
    {
      type: 'IT & Consulting',
      location: 'Bayern',
      year: '2021',
      status: 'Verfügbar',
    },
    {
      type: 'Immobilien',
      location: 'Hamburg',
      year: '2018',
      status: 'Reserviert',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Premium B2B */}
      <section className="pt-24 pb-16 lg:pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#F8FAFA] to-white">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            
            {/* Left: Content (6 columns) */}
            <div className="lg:col-span-6 pt-4">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#E9F7F6] rounded-full mb-6">
                <div className="w-1.5 h-1.5 bg-[#009E9A] rounded-full"></div>
                <span className="text-xs font-semibold text-[#009E9A] uppercase tracking-wider">
                  Unternehmensvermittlung
                </span>
              </div>
              
              <h1 className="text-[32px] md:text-[40px] lg:text-[44px] font-bold text-[#0B1930] mb-5 leading-[1.12] tracking-[-0.02em]">
                Unternehmen kaufen.<br />
                <span className="text-[#009E9A]">Werte</span> verkaufen.<br />
                Zukunft gestalten.
              </h1>
              
              <p className="text-base md:text-lg text-gray-600 mb-6 leading-relaxed max-w-md">
                Professionelle Vermittlung von GmbH-Transaktionen. Diskret, strukturiert und mit persönlicher Begleitung.
              </p>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-500 mb-8">
                <span className="flex items-center gap-1.5">
                  <Shield className="w-4 h-4 text-[#009E9A]" />
                  Diskret
                </span>
                <span className="flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-[#009E9A]" />
                  Persönlich
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-[#009E9A]" />
                  Strukturiert
                </span>
              </div>

              <div className="flex flex-wrap gap-3">
                <Link to="/marktplatz">
                  <Button className="bg-[#0B1930] hover:bg-[#162844] text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors">
                    Marktplatz ansehen
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/kontakt">
                  <Button variant="outline" className="border-[#0B1930]/20 text-[#0B1930] hover:bg-[#E9F7F6] hover:border-[#009E9A] text-sm font-medium px-6 py-2.5 rounded-lg transition-colors">
                    Beratungsgespräch
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: Marketplace Preview (6 columns) */}
            <div className="lg:col-span-6">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {/* Preview Header */}
                <div className="px-5 py-3 border-b border-gray-100 bg-[#F8FAFA]">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Marktplatz-Vorschau</span>
                    <span className="text-xs text-gray-400">Beispielansicht</span>
                  </div>
                </div>
                
                {/* Preview Listings */}
                <div className="divide-y divide-gray-100">
                  {previewListings.map((listing, idx) => (
                    <div key={idx} className="px-5 py-4 hover:bg-[#F8FAFA]/50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded ${
                              listing.status === 'Verfügbar' 
                                ? 'bg-[#E9F7F6] text-[#009E9A]' 
                                : 'bg-gray-100 text-gray-500'
                            }`}>
                              {listing.status}
                            </span>
                            <span className="text-[10px] text-gray-400">{listing.type}</span>
                          </div>
                          <div className="text-sm font-semibold text-[#0B1930] mb-1">
                            Bestands-GmbH {listing.type}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {listing.location}
                            </span>
                            <span>Gründung {listing.year}</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-[10px] text-gray-400 mb-0.5">Kaufpreis</div>
                          <div className="text-sm font-bold text-[#0B1930]">Auf Anfrage</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Preview Footer */}
                <div className="px-5 py-3 border-t border-gray-100 bg-[#F8FAFA]">
                  <Link to="/marktplatz" className="flex items-center justify-center gap-2 text-sm font-medium text-[#009E9A] hover:text-[#008580] transition-colors">
                    Alle Angebote ansehen
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Refined */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100">
        <div className="max-w-[1280px] mx-auto">
          {/* Section Header */}
          <div className="max-w-xl mb-10">
            <h2 className="text-2xl md:text-[28px] font-bold text-[#0B1930] mb-2 tracking-[-0.01em]">
              Ihr Weg zur GmbH
            </h2>
            <p className="text-gray-600">
              Drei Optionen für Ihren unternehmerischen Start oder Ihre Nachfolgeplanung.
            </p>
          </div>

          {/* Service Cards - 3 Column Grid */}
          <div className="grid md:grid-cols-3 gap-5">
            {/* Card 1: Kaufen */}
            <div className="group relative bg-white border border-gray-200 rounded-xl p-6 hover:border-[#009E9A] hover:shadow-md transition-all">
              <div className="w-10 h-10 bg-[#E9F7F6] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#009E9A] transition-colors">
                <Building2 className="w-5 h-5 text-[#009E9A] group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg font-bold text-[#0B1930] mb-2">GmbH kaufen</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-5">
                Übernehmen Sie eine bestehende Gesellschaft mit Historie und starten Sie schneller.
              </p>
              <Link to="/marktplatz">
                <Button variant="outline" className="w-full justify-center border-gray-200 text-[#0B1930] hover:bg-[#E9F7F6] hover:border-[#009E9A] text-sm rounded-lg">
                  Zum Marktplatz
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* Card 2: Verkaufen - Primary */}
            <div className="group relative bg-[#0B1930] rounded-xl p-6 text-white">
              <div className="absolute top-4 right-4">
                <span className="text-[10px] font-semibold bg-[#009E9A] px-2 py-1 rounded">Empfohlen</span>
              </div>
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-5 h-5 text-[#009E9A]" />
              </div>
              <h3 className="text-lg font-bold mb-2">GmbH verkaufen</h3>
              <p className="text-sm text-gray-300 leading-relaxed mb-5">
                Diskrete Vermittlung Ihrer Gesellschaft an geprüfte Interessenten.
              </p>
              <Link to="/seller-onboarding">
                <Button className="w-full justify-center bg-white text-[#0B1930] hover:bg-[#E9F7F6] text-sm font-semibold rounded-lg">
                  Jetzt anbieten
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* Card 3: Gründen */}
            <div className="group relative bg-white border border-gray-200 rounded-xl p-6 hover:border-[#009E9A] hover:shadow-md transition-all">
              <div className="w-10 h-10 bg-[#E9F7F6] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#009E9A] transition-colors">
                <PlusCircle className="w-5 h-5 text-[#009E9A] group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg font-bold text-[#0B1930] mb-2">GmbH gründen</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-5">
                Strukturierte Begleitung von der Vorbereitung bis zur Handelsregistereintragung.
              </p>
              <Link to="/onboarding">
                <Button variant="outline" className="w-full justify-center border-gray-200 text-[#0B1930] hover:bg-[#E9F7F6] hover:border-[#009E9A] text-sm rounded-lg">
                  Formular starten
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section - Compact */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#F8FAFA] border-t border-gray-100">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-2xl font-bold text-[#0B1930] mb-1">Persönlich</div>
              <p className="text-sm text-gray-500">Individuelle Begleitung durch den gesamten Prozess</p>
            </div>
            <div>
              <div className="text-2xl font-bold text-[#0B1930] mb-1">Diskret</div>
              <p className="text-sm text-gray-500">Vertrauliche Behandlung aller Transaktionen</p>
            </div>
            <div>
              <div className="text-2xl font-bold text-[#0B1930] mb-1">Strukturiert</div>
              <p className="text-sm text-gray-500">Klare Prozesse für effiziente Abwicklung</p>
            </div>
            <div>
              <div className="text-2xl font-bold text-[#0B1930] mb-1">Transparent</div>
              <p className="text-sm text-gray-500">Keine versteckten Kosten oder Überraschungen</p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-[#E9F7F6]">
        <div className="max-w-[600px] mx-auto">
          <NewsletterSignup />
        </div>
      </section>

      {/* Contact CTA - Minimal */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100">
        <div className="max-w-[600px] mx-auto text-center">
          <h3 className="text-xl font-bold text-[#0B1930] mb-3">Fragen zur Vermittlung?</h3>
          <p className="text-gray-600 mb-6">
            Vereinbaren Sie ein unverbindliches Beratungsgespräch.
          </p>
          <Link to="/kontakt">
            <Button className="bg-[#0B1930] hover:bg-[#162844] text-white px-6 py-2.5 text-sm rounded-lg">
              Kontakt aufnehmen
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomeTasteSkillV2;
